import { useAuthStore } from "@/shared/auth/authStore";
import { softphoneApi, type BreakStatusDto, type PhoneInfo } from "@/shared/api/softphoneApi";
import { useAsyncFeedbackStore } from "@/shared/feedback/asyncFeedbackStore";
import { currentHeartbeat, heartbeatOnUnload, isHeartbeatRunning, startHeartbeat, stopHeartbeat } from "./heartbeat";
import * as engine from "./sipEngine";
import { isValidDialTarget, normalizeDialTarget } from "./softphoneConfig";
import { useSoftphoneStore, type CallInfo } from "./softphoneStore";
import { unlockAudio } from "./tones";

/**
 * Ações de alto nível do agente (status, pausa, discagem) — combinam API, motor SIP e store.
 * Também concentra o ciclo de vida: boot, desligamento limpo (logout/unload) e logout forçado.
 */

const store = () => useSoftphoneStore.getState();
const toast = (kind: "success" | "error" | "info", msg: string) =>
  useAsyncFeedbackStore.getState().pushToast(kind, msg);

const STASH_KEY = "pm.softphone.stash";
/** Pausa "Disponível" (o backend usa breakId 0 / title "Disponível" para voltar ao ar). */
export const AVAILABLE_BREAK_ID = 0;

type Stash = { sessionStatus: string; sessionInfo: string | null; breakId: number | null; at: number };

function httpStatus(e: unknown): number | undefined {
  return (e as { response?: { status?: number } })?.response?.status;
}

export function isAvailableBreak(b: Pick<BreakStatusDto, "value" | "title"> | null | undefined) {
  const c = String(b?.value || b?.title || "").trim().toLowerCase();
  return c === "disponivel" || c === "disponível";
}

/* ------------------------------------------------------------- engine -- */

engine.setEngineHooks({
  shouldRejectIncoming: () => store().sessionStatus === "PAUSED",
  onCallEstablished: (call) => void enrichCall(call),
  // Como o legado: sem registro o agente fica OFFLINE "Registration Failed" (não "disponível" sem ramal).
  onRegistrationFatal: () => {
    store().patch({ pendingApproval: null });
    store().setSession("OFFLINE", "Registration Failed");
  },
});

async function enrichCall(call: CallInfo) {
  const sysid = call.remote.sysid;
  if (sysid && sysid.startsWith("pl")) {
    softphoneApi
      .listPayloads(sysid)
      .then((payload) => {
        if (store().call?.id === call.id) store().patchCall({ payload });
      })
      .catch(() => toast("error", "Não foi possível ler os dados da chamada (payload)."));
  }
  // Fila de origem (transferências). Sem callid o backend procura pelo início da chamada,
  // que pode demorar a aparecer — até 3 tentativas (o legado tentava indefinidamente).
  const startedAt = new Date(call.answeredAt ?? call.createdAt);
  for (let attempt = 0; attempt < 3; attempt++) {
    if (store().call?.id !== call.id) return;
    try {
      const qf = await softphoneApi.getTransferredFrom(call.remote.callid, startedAt);
      if (qf) {
        if (store().call?.id === call.id) store().patchCall({ queueFrom: qf });
        return;
      }
    } catch {
      /* segue tentando */
    }
    if (call.remote.callid) return;
    await new Promise((r) => window.setTimeout(r, 2000));
  }
}

/* ------------------------------------------------------------- status -- */

function ensureRegistered(): boolean {
  const s = store();
  if (!s.phone) {
    toast("error", "Seu usuário não tem ramal associado. Fale com o supervisor.");
    return false;
  }
  if (s.ownership !== "this") {
    toast("error", "O softphone está ativo em outra aba deste navegador.");
    return false;
  }
  if (!engine.isUARunning() || s.reg === "failed" || s.reg === "idle") {
    const displayName = useAuthStore.getState().displayName ?? useAuthStore.getState().tokenSubject;
    const res = engine.startUA(s.phone, displayName);
    if (!res.ok) {
      store().patch({ reg: "failed", regError: res.message });
      toast("error", res.message);
      return false;
    }
  }
  if (!isHeartbeatRunning()) startHeartbeat({ onUnauthorized: () => void handleUnauthorized() });
  return true;
}

/** "Disponível": registra o ramal (se preciso) e sai de pausa. */
export async function goAvailable() {
  unlockAudio();
  const prev = store().sessionStatus;
  if (!ensureRegistered()) return;
  store().patch({ pendingApproval: null });
  store().setSession("ONLINE", null);
  if (prev === "PAUSED") {
    // Presença no ServiceNow; 400 = integração não configurada (normal fora da RD).
    softphoneApi.changeStatusPhone(AVAILABLE_BREAK_ID).catch(() => {});
  }
}

export async function goOffline(reason: string | null = null) {
  if (store().call) {
    toast("error", "Encerre a chamada atual antes de ficar offline.");
    return;
  }
  store().patch({ pendingApproval: null });
  store().setSession("OFFLINE", reason);
  engine.stopUA(true);
}

/** Aplica a pausa localmente (heartbeat PAUSED + value) e notifica o backend/ServiceNow. */
export async function applyPause(brk: BreakStatusDto, notifyServer = true) {
  unlockAudio();
  if (!ensureRegistered()) return;
  const info = brk.value || brk.title || null;
  const prev = { status: store().sessionStatus, info: store().sessionInfo, brk: store().currentBreak };
  store().patch({ pendingApproval: null });
  store().setSession("PAUSED", info, brk);
  if (!notifyServer || brk.breakId == null) return;
  try {
    await softphoneApi.changeStatusPhone(brk.breakId);
  } catch (e) {
    if (httpStatus(e) === 422) {
      // O backend exigiu aprovação (e já registrou o pedido): volta ao estado anterior e aguarda.
      store().setSession(prev.status, prev.info, prev.brk);
      store().patch({
        pendingApproval: { breakId: brk.breakId, title: brk.title || brk.value || "Pausa", requestedAt: Date.now() },
      });
      toast("info", "Esta pausa precisa de aprovação do supervisor. Aguardando…");
    }
    // 400 (ServiceNow não configurado) e demais erros: a pausa vale pelo heartbeat.
  }
}

/** Pedido de pausa vindo da UI — respeita ENABLED / NEED_APPROVE / DISABLED. */
export async function requestPause(brk: BreakStatusDto) {
  // Sem breakId (lista do ramal, quando findBreakByUser falha): pausa só via heartbeat.
  if (brk.breakId == null) {
    await applyPause({ ...brk, status: "ENABLED" }, false);
    return;
  }
  switch (brk.status) {
    case "DISABLED":
      toast("error", "Esta pausa não está disponível agora (limite do dia atingido ou recusada).");
      return;
    case "NEED_APPROVE":
      try {
        await softphoneApi.createBreakApproval(brk.breakId);
        store().patch({
          pendingApproval: { breakId: brk.breakId, title: brk.title || brk.value || "Pausa", requestedAt: Date.now() },
        });
        toast("info", `Pedido de pausa "${brk.title ?? brk.value}" enviado ao supervisor.`);
      } catch {
        toast("error", "Não foi possível solicitar a aprovação da pausa.");
      }
      return;
    default:
      await applyPause(brk);
  }
}

export function cancelPendingApproval() {
  store().patch({ pendingApproval: null });
}

/**
 * Resultado do polling de aprovação: aprovada → entra na pausa; recusada → avisa.
 * Retorna true quando o pedido foi resolvido.
 */
export async function checkPendingApproval(): Promise<boolean> {
  const pending = store().pendingApproval;
  if (!pending) return true;
  let list: BreakStatusDto[];
  try {
    list = await softphoneApi.findBreakByUser();
  } catch {
    return false;
  }
  const b = list.find((x) => x.breakId === pending.breakId);
  if (!b) return false;
  if (b.approved === true || b.status === "ENABLED") {
    toast("success", `Pausa "${pending.title}" aprovada.`);
    await applyPause(b, true);
    return true;
  }
  if (b.approved === false) {
    store().patch({ pendingApproval: null });
    toast("error", `Pausa "${pending.title}" recusada pelo supervisor.`);
    return true;
  }
  return false;
}

/** Mensagem `{isPaused, breakDto}` em /user/queue/messages (troca de status feita pelo ServiceNow). */
export function applyServerPauseMessage(msg: { isPaused?: boolean; breakDto?: Record<string, unknown> | null }) {
  const brk = (msg.breakDto ?? null) as (BreakStatusDto & { breakSysid?: string }) | null;
  if (msg.isPaused === true && brk && !isAvailableBreak(brk)) {
    void applyPause({ ...brk, status: "ENABLED" }, false);
  } else if (msg.isPaused === false || isAvailableBreak(brk)) {
    if (store().sessionStatus === "PAUSED") void goAvailable();
  }
}

/* ------------------------------------------------------------ discagem -- */

export function dial(raw: string): boolean {
  unlockAudio();
  if (!isValidDialTarget(raw)) {
    toast("error", "Número inválido. Use apenas dígitos, * ou #.");
    return false;
  }
  const s = store();
  if (s.reg !== "registered") {
    toast("error", "O ramal não está registrado. Fique disponível ou em pausa para discar.");
    return false;
  }
  const { target } = normalizeDialTarget(raw);
  const res = engine.call(target);
  if (!res.ok) {
    toast("error", res.message);
    return false;
  }
  return true;
}

/* -------------------------------------------------------- ciclo de vida -- */

function writeStash() {
  const s = store();
  try {
    const stash: Stash = {
      sessionStatus: s.sessionStatus,
      sessionInfo: s.sessionInfo,
      breakId: s.currentBreak?.breakId ?? null,
      at: Date.now(),
    };
    sessionStorage.setItem(STASH_KEY, JSON.stringify(stash));
  } catch {
    /* ignore */
  }
}

function readStash(): Stash | null {
  try {
    const raw = sessionStorage.getItem(STASH_KEY);
    sessionStorage.removeItem(STASH_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Stash;
    return Date.now() - v.at < 60_000 ? v : null;
  } catch {
    return null;
  }
}

/**
 * Boot (aba dona): carrega o ramal e restaura o estado. Recarregar a página dentro de 60s volta ao
 * mesmo status (pausa inclusive, sem contar uma pausa nova); senão, agente puro (só ROLE_USER) entra
 * "Disponível" automaticamente como no softphone legado, e ADMIN/SUPER começam offline.
 */
export async function bootSoftphone(opts: { autoOnline: boolean }) {
  let phone: PhoneInfo | null;
  try {
    phone = await softphoneApi.phoneByLogin();
  } catch {
    store().patch({ phoneLoaded: true, phoneError: "Não foi possível carregar os dados do ramal." });
    return;
  }
  store().patch({ phone, phoneLoaded: true, phoneError: null });
  if (!phone) return;

  const stash = readStash();
  if (stash?.sessionStatus === "PAUSED" && stash.sessionInfo) {
    let brk: BreakStatusDto = { breakId: stash.breakId, value: stash.sessionInfo, title: stash.sessionInfo, status: "ENABLED" };
    try {
      const list = await softphoneApi.findBreakByUser();
      brk = list.find((b) => b.breakId === stash.breakId) ?? brk;
    } catch {
      /* sem ROLE_USER / offline: usa o que tinha */
    }
    await applyPause(brk, false);
    return;
  }
  if (stash?.sessionStatus === "ONLINE" || (!stash && opts.autoOnline)) {
    await goAvailable();
  }
}

/** Logout normal: OFFLINE "Logout" no heartbeat antes de o token ser descartado + des-registro SIP. */
export async function shutdownSoftphone(reason = "Logout") {
  const wasActive = isHeartbeatRunning();
  engine.stopUA(true);
  store().setSession("OFFLINE", reason);
  if (wasActive) {
    await Promise.race([
      stopHeartbeat({ ...currentHeartbeat(), sessionStatus: "OFFLINE", sessionInfo: reason, phoneStatus: "OFFLINE" }),
      new Promise((r) => window.setTimeout(r, 2500)),
    ]);
  }
  store().reset();
}

/**
 * Fechamento/recarga da aba. Em pausa NÃO mandamos OFFLINE: recarregar a página não pode gerar
 * uma pausa nova (o limite diário conta atividades PAUSED); se a aba foi fechada de fato, o janitor
 * do backend fecha com "Connection lost" em ~60s. Fora de pausa, OFFLINE imediato.
 */
export function onPageHide() {
  writeStash();
  const paused = store().sessionStatus === "PAUSED";
  heartbeatOnUnload(
    paused ? null : { ...currentHeartbeat(), sessionStatus: "OFFLINE", sessionInfo: null, phoneStatus: "OFFLINE" },
  );
  engine.stopUA(true);
}

/** Logout forçado pelo supervisor (o backend já gravou OFFLINE "Forced logout"). */
export async function handleForcedLogout() {
  if (store().forcedLogout) return;
  store().patch({ forcedLogout: true });
  engine.stopUA(true);
  await stopHeartbeat();
  toast("error", "Sua sessão foi encerrada pelo supervisor (logout forçado).");
  useAuthStore.getState().logout();
}

async function handleUnauthorized() {
  engine.stopUA(true);
  await stopHeartbeat();
  if (useAuthStore.getState().token) {
    toast("error", "Sessão expirada. Entre novamente.");
    useAuthStore.getState().logout();
  }
}

/** Aba perdeu a posse (ou o usuário saiu): para tudo sem heartbeat final. */
export async function releaseSoftphone() {
  engine.stopUA(true);
  await stopHeartbeat();
}
